/**
 * The second opening-animation overlay — `assets/brand/design-drafts-2026-08/bluecheck.svg` ported onto the shipped Home,
 * selectable beside `GateOpenOverlay` through `theme/openAnimation.ts`.
 *
 * **It reports the delivery receipt and nothing more (D134/D140).** The check draws on
 * `outcome: 'opened'`, which is the controller acknowledging the command in ~300 ms — not the
 * leaf finishing its travel. So there is no "gate is open" copy here either, the whole overlay is
 * `aria-hidden`, and the tile underneath is the thing that speaks. Same props, same three beats,
 * same exit timing as the overlay beside it: the switcher is a render and nothing else.
 *
 * ## What the source is, and what a 3.036 s loop had to become
 *
 * The source is one SMIL loop with no states in it: a disc scales up, a ring strokes around it, a
 * white check is drawn with an overshoot and a settle, and eight spokes burst outward — all on
 * one timeline that then repeats. The product has three beats and the first of them runs for an
 * unknown time, so the loop is cut where the source's own timeline already divides:
 *
 *  - **The wait is the ring.** The disc settles in on entrance (the source's own scale spline,
 *    played once) and then an arc orbits its rim indefinitely. That is the only part of the
 *    source that can honestly run for an unknown duration — it says *working*, not *nearly done*.
 *  - **The answer is the check and the burst**, on the `open` class: the rim closes into a
 *    complete ring, the check draws past its endpoint and pulls back, and the spokes fire.
 *  - **A refusal leaves with no answer beat at all**, exactly as `GateOpenOverlay` does, and for
 *    the same reason: a full-screen refusal surface is something no PRD specifies.
 *
 * **Three things dropped or changed from the source, each on purpose:**
 *
 *  - **The SMIL is not ported.** The source draws every stroke with ~300 `<set fill="freeze">`
 *    elements stepping a `d` attribute at 30 fps — a rasterised animation wearing vector markup.
 *    The idiom here is `GateOpenOverlay`'s: one `<animate>` for the one thing that loops, and CSS
 *    `@keyframes` over `stroke-dashoffset` for everything with a beginning and an end. The check
 *    is a two-segment path, so its overshoot-and-settle is one `stroke-dashoffset` curve run past
 *    zero and back — see `TICK_REST` below.
 *  - **The spokes moved outward.** In the source they fire at `r 124…355` (of 1080) *while the
 *    disc is still scaling up*, and the growing disc swallows them; here the disc belongs to the
 *    wait beat and is already seated when the burst fires, so on the source's radii the whole
 *    burst would be behind it. They start just clear of the rim instead.
 *  - **The spokes fire after the check's elbow, not before the check.** The source bursts first
 *    and draws the check into the quiet afterwards. Here the burst is the check's own energy
 *    leaving the disc, which is what a receipt beat wants: one gesture, not two.
 *
 * A fourth change is the ring's ink, and it is the stylesheet's — the two-inks block on
 * `.fx.check` carries it. The short version: the source's blue ring is legible only because the
 * source's disc is still growing underneath it, and a blue ring on a seated disc renders the wait
 * beat as a static disc. It is white here.
 *
 * ## Colour — one blue, and the answer is not a hue change
 *
 * `GateOpenOverlay` waits in blue and answers in turquoise, because its wait and its answer are
 * two different objects (a band on the leaves, then a cloud). Here they are **one object**: the
 * disc that carries the wait is the disc the check is drawn on. Re-hueing it on the answer would
 * read as the mark being wrong rather than as the state changing, so the blue holds throughout
 * and **the answer is carried by the white check and the burst** — which is the source's own
 * division of labour and the reason the mark reads at all.
 *
 * The blue is **`#2f88e4`**, the 52% stop of the shipped band's own `sg-gaterun` gradient: the
 * product's in-flight blue at full strength, not an invented value. Two alternatives were
 * measured against it. The source's `#0f75bc` is a dark, saturated navy — it sinks into graphite
 * and reads as ink rather than as light on cream. `#4aa3f0`, the shipped band's flood colour, is
 * a *glow* value: as a 168 px solid disc it is a pale sky patch on a cream page, and white on it
 * falls to about 2.3:1, which is not a mark. `#4aa3f0` is kept for exactly what it already is —
 * the flood colour of the glows below.
 *
 * Everything after that is **weight, never hue** (see the stylesheet's graphite block): alphas
 * come down over a dark ground and the scrim inverts its job, the way `.fx.reveal`'s does.
 *
 * ## Two mechanical rules this file exists inside
 *
 *  - **The defs are JSX elements, never a string.** A `<filter>` built from a template and
 *    inserted with `createContextualFragment` parses in *HTML* context and comes back as unknown
 *    nodes carrying no `flood-color`; the reference still resolves and the glow draws black.
 *    Nothing warns. React creates SVG children in the SVG namespace, so the trap cannot reach
 *    here — this note is why they are elements rather than markup.
 *  - **The glows are SVG `<filter>`s referenced by the `filter` ATTRIBUTE, never CSS `filter` on
 *    an SVG element.** Safari parses the CSS declaration, reports it live in computed style, and
 *    never paints it. `opacity` on an SVG element is fine and is used freely.
 *
 * The ids are scoped `sgc-` where the shipped overlay's are `sg-`, so the two can never collide
 * even if a future caller mounts both; they are rendered inside the overlay so the per-scheme
 * `flood-opacity` handles stated on `.fx.check` reach them.
 */
import { useEffect, useRef, useState } from 'react'
import type { GateOpenOutcome } from './GateOpenOverlay'

/** The floor under the wait beat — see `GateOpenOverlay`'s own note. */
const MIN_WAIT_MS = 600
const ANSWER_HOLD_MS = 1900
const FADE_OUT_MS = 340

/* ====================================================================== *
 * The mark.
 *
 * One 240-unit square with everything centred on (120, 120). The source is 1080 wide and its
 * objects are drawn at 1080 and then scaled by group transforms, so every number below is the
 * source's own measurement carried through one factor (240 / 1080) — the disc's radius, the
 * rim's stroke, the check's three vertices and its stroke, the spokes' stroke. The one departure
 * is the spokes' radii, for the reason in the header.
 * ====================================================================== */

/** The disc, and the rim that straddles its edge — the source draws both at the same radius. */
const CENTRE = 120
const DISC_R = 72
const RIM_W = 8.67

/**
 * The check, as the source draws it at full extension: start, elbow, and the **overshoot** tip.
 * The settled tip is not a vertex — it is a point 87% of the way along this path, which is what
 * makes the overshoot expressible as a `stroke-dashoffset` curve rather than as a moving `d`.
 */
const TICK_PATH = 'M 81.78 130.67 L 109.33 158.22 L 165.33 91.56'
const TICK_W = 13.11
/**
 * Where the check comes to rest, as a `stroke-dashoffset` on a `pathLength` of 1.
 *
 * The source's stroke reaches (220, -140) and then retracts to (172.6, -83.5) over the following
 * half second. Measured along the path above, the resting tip is at 0.870 of its length, so the
 * settle is `stroke-dashoffset: 0.130` — the last 13% of the drawn stroke retracted, which is the
 * same pixels the source's `d` gave back, arrived at without moving a vertex.
 *
 * **A dash offset shortens the stroke from its END, which is the only reason this works.** The
 * dash starts at the path's start point, so pulling the offset positive un-draws the tip and
 * leaves the elbow and the short leg exactly where they were. Animating the path's own geometry
 * instead — the source's approach — moves the whole curve and is what 300 `<set>` elements buy.
 */
const TICK_REST = 0.13

/**
 * Eight spokes at 45°, computed rather than typed so the ring cannot drift out of phase.
 *
 * `SPOKE_IN` clears the rim's outer edge (72 + 8.67 / 2 = 76.3) by about four units — a visible
 * gap, so the burst reads as leaving the disc rather than growing out of it. `SPOKE_OUT` is
 * shorter than the source's 51-unit streak because the source's runs off this box; 28 units with
 * the source's own 10-unit round-capped stroke is still a streak rather than a tick.
 *
 * **Radial, so the composition has no reading direction.** The shell is RTL and this overlay is
 * mirrored by nothing: there is no `left`/`right` anywhere in it, and eight evenly spaced rays
 * look the same under either direction.
 */
const SPOKE_IN = 80
const SPOKE_OUT = 108
const SPOKE_W = 10.11
const SPOKES = Array.from({ length: 8 }, (_, index) => {
  const angle = (index * Math.PI) / 4
  const at = (radius: number): readonly [number, number] => [
    Number((CENTRE + radius * Math.cos(angle)).toFixed(2)),
    Number((CENTRE + radius * Math.sin(angle)).toFixed(2)),
  ]
  const [x1, y1] = at(SPOKE_IN)
  const [x2, y2] = at(SPOKE_OUT)
  return { key: index, x1, y1, x2, y2 }
})

/**
 * The two glows.
 *
 * **One `<filter>` per element serves both schemes here**, unlike the shipped overlay's band,
 * which needs a hidden duplicate because its two schemes differ in `stdDeviation` and no CSS
 * property reaches that attribute. Nothing here needs that: the disc is a solid 168 px shape and
 * its halo is a *seat* rather than the thing being looked at, so the scheme delta is `flood-
 * opacity` alone — a real CSS property, reached by the handles on `.fx.check`. The attributes
 * written below carry the LIGHT values, deliberately: they are what renders on an engine that
 * declines the CSS property, and the light weights in both schemes is a degradation rather than a
 * break. **The attribute is the one that is not allowed to drift** from the stylesheet's handle.
 *
 * The flood colour is `#4aa3f0` — the shipped band's, unchanged. A glow is exactly what that
 * value already is in this product, and using the disc's own `#2f88e4` for it would tint the halo
 * toward the shape it is meant to lift off the page.
 *
 * `drop-shadow()`'s third length is a blur RADIUS and `feDropShadow`'s `stdDeviation` is a
 * standard deviation, so every radius carried over from CSS halves.
 */
function Defs(): JSX.Element {
  return (
    <svg className="fx-defs" width="0" height="0" aria-hidden="true" focusable="false">
      <defs>
        <filter id="sgc-discglow" x="-60%" y="-60%" width="220%" height="220%">
          <feDropShadow
            className="tight"
            dx={0}
            dy={0}
            stdDeviation={7}
            floodColor="#4aa3f0"
            floodOpacity={0.55}
          />
          <feDropShadow
            className="wide"
            dx={0}
            dy={0}
            stdDeviation={17}
            floodColor="#4aa3f0"
            floodOpacity={0.38}
          />
        </filter>

        {/* The orbiting arc's own glow, tighter than the disc's: this one IS a thin travelling
            stroke, so the shipped band's finding applies to it — over a dark ground a wide halo
            becomes most of what is on screen and the travelling core becomes the faint part.
            Reaching for a wider glow to make it more visible is the move that makes it worse. */}
        <filter id="sgc-orbitglow" x="-60%" y="-60%" width="220%" height="220%">
          <feDropShadow
            className="tight"
            dx={0}
            dy={0}
            stdDeviation={4}
            floodColor="#4aa3f0"
            floodOpacity={0.7}
          />
        </filter>
      </defs>
    </svg>
  )
}

export interface CheckOpenOverlayProps {
  /** The open's own state. `pending` holds the wait; `opened`/`refused` each start an exit. */
  readonly outcome: GateOpenOutcome
  /** Called once the exit has finished. The caller unmounts on it — nothing self-unmounts. */
  readonly onDone: () => void
}

export function CheckOpenOverlay({ outcome, onDone }: CheckOpenOverlayProps): JSX.Element {
  const [answered, setAnswered] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const mountedAt = useRef(Date.now())
  const timers = useRef<number[]>([])
  /**
   * `onDone` is a fresh closure on every render of the caller, and this effect starts timers —
   * depending on its identity would restart the whole exit choreography every time the tile
   * underneath changed state, which is exactly when it changes.
   */
  const done = useRef(onDone)
  done.current = onDone

  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const id of pending) window.clearTimeout(id)
    }
  }, [])

  useEffect(() => {
    if (outcome === 'pending') return
    const after = (ms: number, run: () => void): void => {
      timers.current.push(window.setTimeout(run, ms))
    }
    if (outcome === 'refused') {
      // Every refusal — denied, `gate-busy`, `not-authorized`, offline: the overlay leaves
      // without an answer beat and the tile's own denied state carries the message.
      setLeaving(true)
      after(FADE_OUT_MS, () => done.current())
      return
    }
    const wait = Math.max(0, MIN_WAIT_MS - (Date.now() - mountedAt.current))
    after(wait, () => setAnswered(true))
    after(wait + ANSWER_HOLD_MS, () => setLeaving(true))
    after(wait + ANSWER_HOLD_MS + FADE_OUT_MS, () => done.current())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome])

  return (
    <div className={`fx check${answered ? ' open' : ''}${leaving ? ' out' : ''}`} aria-hidden="true">
      <Defs />
      <div className="scrim" />
      {/* `preserveAspectRatio` is left at its default, unlike the shipped overlay's scene: that
          one is a composition of two objects whose spacing has to survive any frame shape, and
          this is a single centred mark. A stretched circle is not a mark. */}
      <svg className="mark" viewBox="0 0 240 240" aria-hidden="true">
        {/* Drawn first, so the burst leaves from BEHIND the disc: a spoke whose inner cap is
            covered has no visible beginning, which is what makes it read as thrown rather than
            as drawn. It clears the rim by four units, so only the cap is ever hidden. */}
        <g className="spokes">
          {SPOKES.map((spoke) => (
            <line
              key={spoke.key}
              className="spoke"
              x1={spoke.x1}
              y1={spoke.y1}
              x2={spoke.x2}
              y2={spoke.y2}
              pathLength={1}
              strokeWidth={SPOKE_W}
              strokeDasharray="0.5 1"
              strokeDashoffset={0.5}
            />
          ))}
        </g>
        <circle className="disc" cx={CENTRE} cy={CENTRE} r={DISC_R} filter="url(#sgc-discglow)" />
        {/* The rim and the orbit are the same circle twice, and the split is the file's idiom
            rather than a duplicate: the orbit is the one thing that loops for an unknown time, so
            it is an `<animate>`; the rim has a beginning and an end, so it is a CSS keyframe on
            the `open` class. One element cannot be both without a JS clock reading the beat. */}
        <circle
          className="orbit"
          cx={CENTRE}
          cy={CENTRE}
          r={DISC_R}
          pathLength={1}
          strokeWidth={RIM_W}
          strokeDasharray="0.22 0.78"
          strokeDashoffset={1}
          filter="url(#sgc-orbitglow)"
        >
          <animate attributeName="stroke-dashoffset" from="1" to="0" dur="1.45s" repeatCount="indefinite" />
        </circle>
        <circle
          className="rim"
          cx={CENTRE}
          cy={CENTRE}
          r={DISC_R}
          pathLength={1}
          strokeWidth={RIM_W}
          strokeDasharray="1 1"
          strokeDashoffset={1}
        />
        <path
          className="tick"
          d={TICK_PATH}
          pathLength={1}
          strokeWidth={TICK_W}
          strokeDasharray="1 1"
          strokeDashoffset={1}
        />
      </svg>
    </div>
  )
}

/**
 * The resting offset, exported so the pair can be asserted rather than described.
 *
 * The number lives in two places by necessity — here, where it is derived from the path, and in
 * `global.css`, where the keyframe that settles the check has to end on it. CSS cannot read a
 * module, so the seam is a test: `CheckOpenOverlay.test.tsx` reads the stylesheet's own last
 * keyframe and compares it to this. A comment in two files is how the last such pair drifted.
 */
export const TICK_SETTLE_OFFSET = TICK_REST
